/**
 * 計算フィールド 計算エンジン（#3145）の構文解析（再帰下降パーサ）。
 * 文法（設計書 §4.2.5）:
 *   formula        := comparison
 *   comparison     := concat ( ("=" | "!=" | "<" | ">" | "<=" | ">=") concat )?  // 非結合（#3358）
 *   concat         := additive ( "&" additive )*
 *   additive       := multiplicative ( ("+" | "-") multiplicative )*
 *   multiplicative := power ( ("*" | "/") power )*
 *   power          := unary ( "^" power )?          // 右結合
 *   unary          := ("+" | "-")? primary
 *   primary        := number | string | fieldRef | funcCall | "(" formula ")"
 * ⚠️ functions/src/utils/calculation と main/src/utils/calculation は同一内容。
 *    片方だけ変更しないこと（変更時は両方へ反映し、共通テストで一致を確認する）。
 */
import {
  CALCULATION_FORMULA_MAX_LENGTH,
  CalculationFunctionName,
  calculationErrorCode,
  calculationFunction,
  calculationFunctionArity,
  CALCULATION_FUNCTION_MAX_ARGS,
} from './constants';
import { CalculationFormulaError } from './calculationError';
import { Token, tokenize } from './tokenizer';

export type AstNode =
  | { type: 'number'; value: number }
  | { type: 'string'; value: string }
  | { type: 'fieldRef'; fieldCode: string }
  // 明細の集約参照（#3358）。{明細.金額} のようにドットで親子をつなぐ。
  // SUM の引数の中でのみ使える（検証は validateFormula が行う）
  | { type: 'aggregateRef'; rawCode: string }
  | { type: 'unary'; operator: '+' | '-'; operand: AstNode }
  | {
      type: 'binary';
      // 比較演算子（#3358）は最も優先順位が低く、結果は 1 / 0 の数値になる
      operator: '+' | '-' | '*' | '/' | '^' | '&' | '=' | '!=' | '<' | '>' | '<=' | '>=';
      left: AstNode;
      right: AstNode;
    }
  | { type: 'funcCall'; name: CalculationFunctionName; args: AstNode[] };

const FUNCTION_NAMES: string[] = Object.values(calculationFunction);

/** 比較演算子（#3358）。2文字のものを先に試す */
const COMPARISON_OPERATORS = ['<=', '>=', '!=', '=', '<', '>'] as const;

/** トークン列を読み進めるカーソル */
class TokenReader {
  private readonly tokens: Token[];
  private index = 0;

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  peek(): Token | undefined {
    return this.tokens[this.index];
  }

  next(): Token {
    const token = this.tokens[this.index];
    if (!token) {
      throw new CalculationFormulaError(calculationErrorCode.syntax, '式が途中で終わっています');
    }
    this.index++;
    return token;
  }

  isOperator(value: string): boolean {
    const token = this.peek();
    return token?.type === 'operator' && token.value === value;
  }

  isEnd(): boolean {
    return this.index >= this.tokens.length;
  }
}

/**
 * 比較（#3358）。最も優先順位が低く、**非結合**にする。
 *
 * a < b < c を許すと (a<b)<c = 1<c と解釈され、書いた人の意図と違う結果が
 * 黙って出るため構文エラーにする（kintone と同じ）。
 */
const parseComparison = (reader: TokenReader): AstNode => {
  const node = parseConcat(reader);
  const operator = COMPARISON_OPERATORS.find(op => reader.isOperator(op));
  if (operator === undefined) return node;

  reader.next();
  const right = parseConcat(reader);

  // 非結合。2つ目の比較演算子が続いたらエラーにする
  if (COMPARISON_OPERATORS.some(op => reader.isOperator(op))) {
    throw new CalculationFormulaError(
      calculationErrorCode.syntax,
      '比較演算子は1つの式に1つだけ書けます',
    );
  }
  return { type: 'binary', operator, left: node, right };
};

const parseConcat = (reader: TokenReader): AstNode => {
  let node = parseAdditive(reader);
  while (reader.isOperator('&')) {
    reader.next();
    node = { type: 'binary', operator: '&', left: node, right: parseAdditive(reader) };
  }
  return node;
};

const parseAdditive = (reader: TokenReader): AstNode => {
  let node = parseMultiplicative(reader);
  while (reader.isOperator('+') || reader.isOperator('-')) {
    const operator = reader.next().value as '+' | '-';
    node = { type: 'binary', operator, left: node, right: parseMultiplicative(reader) };
  }
  return node;
};

const parseMultiplicative = (reader: TokenReader): AstNode => {
  let node = parsePower(reader);
  while (reader.isOperator('*') || reader.isOperator('/')) {
    const operator = reader.next().value as '*' | '/';
    node = { type: 'binary', operator, left: node, right: parsePower(reader) };
  }
  return node;
};

const parsePower = (reader: TokenReader): AstNode => {
  const node = parseUnary(reader);
  if (reader.isOperator('^')) {
    reader.next();
    // 右結合（2^3^2 = 2^(3^2)）
    return { type: 'binary', operator: '^', left: node, right: parsePower(reader) };
  }
  return node;
};

const parseUnary = (reader: TokenReader): AstNode => {
  if (reader.isOperator('+') || reader.isOperator('-')) {
    const operator = reader.next().value as '+' | '-';
    return { type: 'unary', operator, operand: parsePrimary(reader) };
  }
  return parsePrimary(reader);
};

const parsePrimary = (reader: TokenReader): AstNode => {
  const token = reader.next();

  if (token.type === 'number') {
    return { type: 'number', value: Number(token.value) };
  }

  if (token.type === 'string') {
    return { type: 'string', value: token.value };
  }

  if (token.type === 'fieldRef') {
    return { type: 'fieldRef', fieldCode: token.value };
  }

  if (token.type === 'funcName') {
    return parseFuncCall(reader, token);
  }

  if (token.type === 'lparen') {
    const node = parseComparison(reader);
    const closing = reader.next();
    if (closing.type !== 'rparen') {
      throw new CalculationFormulaError(calculationErrorCode.syntax, '括弧が閉じられていません');
    }
    return node;
  }

  throw new CalculationFormulaError(
    calculationErrorCode.syntax,
    `解釈できないトークンがあります: ${token.value}`,
  );
};

const parseFuncCall = (reader: TokenReader, nameToken: Token): AstNode => {
  const upperName = nameToken.value.toUpperCase();
  if (!FUNCTION_NAMES.includes(upperName)) {
    throw new CalculationFormulaError(
      calculationErrorCode.syntax,
      `使用できない関数です: ${nameToken.value}`,
    );
  }
  const name = upperName as CalculationFunctionName;

  const opening = reader.next();
  if (opening.type !== 'lparen') {
    throw new CalculationFormulaError(
      calculationErrorCode.syntax,
      `関数 ${name} には括弧が必要です`,
    );
  }

  const args: AstNode[] = [parseComparison(reader)];
  while (reader.peek()?.type === 'comma') {
    reader.next();
    args.push(parseComparison(reader));
  }

  const closing = reader.next();
  if (closing.type !== 'rparen') {
    throw new CalculationFormulaError(
      calculationErrorCode.syntax,
      `関数 ${name} の括弧が閉じられていません`,
    );
  }

  // SUM の引数に書かれた項目参照は「明細の全行」を指す集約参照として扱う（#3358）。
  //
  // 子項目のフィールドコードは既に `親.子` の形が標準（fieldCode.ts）なので、
  // 記法だけでは行内参照（{明細.単価} * {明細.数量}）と区別できない。
  // SUM の中かどうかという文脈で読み替える。
  const resolvedArgs =
    name === 'SUM'
      ? args.map(arg =>
          arg.type === 'fieldRef'
            ? ({ type: 'aggregateRef', rawCode: arg.fieldCode } as AstNode)
            : arg,
        )
      : args;

  // 引数の数は範囲で検証する（#3358 で可変長に対応）
  const arity = calculationFunctionArity[name];
  if (args.length < arity.min || (arity.max !== undefined && args.length > arity.max)) {
    const expected = arity.max === undefined ? `${arity.min} 個以上` : `${arity.min} 個`;
    throw new CalculationFormulaError(
      calculationErrorCode.syntax,
      `関数 ${name} の引数は ${expected}です`,
    );
  }
  if (args.length > CALCULATION_FUNCTION_MAX_ARGS) {
    throw new CalculationFormulaError(
      calculationErrorCode.limitExceeded,
      `関数の引数は ${CALCULATION_FUNCTION_MAX_ARGS} 個までです`,
    );
  }

  return { type: 'funcCall', name, args: resolvedArgs };
};

/** 計算式を AST に変換する。上限超過・構文エラーは CalculationFormulaError を投げる */
export const parseFormula = (formula: string): AstNode => {
  if (formula.length > CALCULATION_FORMULA_MAX_LENGTH) {
    throw new CalculationFormulaError(
      calculationErrorCode.limitExceeded,
      `計算式は ${CALCULATION_FORMULA_MAX_LENGTH} 文字以内で入力してください`,
    );
  }

  const tokens = tokenize(formula);
  if (tokens.length === 0) {
    throw new CalculationFormulaError(calculationErrorCode.syntax, '式が空です');
  }

  const reader = new TokenReader(tokens);
  const node = parseComparison(reader);
  if (!reader.isEnd()) {
    throw new CalculationFormulaError(
      calculationErrorCode.syntax,
      `解釈できないトークンがあります: ${reader.peek()?.value ?? ''}`,
    );
  }
  return node;
};
